/**
 * 维度 / 指标批量参与 Cube：与表头 `rowSelect` 列逻辑一致。
 * 勾选态按 `batchKeys` 与 `fieldsValue` 比对；点击对当前区域全部列做全选 / 全取消。
 */
import React, { useMemo } from 'react';
import { Checkbox } from 'tea-component';

import type { TStepFieldFormatMessage } from '../types';

export interface IFieldConfigBatchSelectCheckboxProps {
  /** 当前区域（维度或指标）全部 `source_column` */
  batchKeys: string[];
  fieldsValue: string[];
  changeFieldsValue: (next: string[]) => void;
  formatMessage: TStepFieldFormatMessage;
  /** 表头列内 / 区块工具栏 */
  variant?: 'header' | 'toolbar';
}

const FieldConfigBatchSelectCheckbox: React.FC<IFieldConfigBatchSelectCheckboxProps> = ({
  batchKeys,
  fieldsValue,
  changeFieldsValue,
  formatMessage,
  variant = 'header',
}) => {
  const hasBatchKeys = (batchKeys?.length ?? 0) > 0;
  const fieldsValueSet = useMemo(() => new Set(fieldsValue), [fieldsValue]);

  /** 当前区域列是否已全部勾选进 `fieldsValue` */
  const isAllSelected = useMemo(
    () => hasBatchKeys && batchKeys.every((k) => fieldsValueSet.has(k)),
    [batchKeys, fieldsValueSet, hasBatchKeys],
  );

  /** 部分选中：有列已勾选但未覆盖 `batchKeys` 全部 */
  const isPartialSelected = useMemo(() => {
    if (!hasBatchKeys || isAllSelected) {
      return false;
    }
    return batchKeys.some((k) => fieldsValueSet.has(k));
  }, [batchKeys, fieldsValueSet, hasBatchKeys, isAllSelected]);

  const selectAllText = formatMessage({ id: 'dbl_model_agent_step2_select_all' });
  const wrapClass =
    variant === 'toolbar'
      ? 'dbl-field-config-row-select dbl-field-config-batch-select-toolbar'
      : 'dbl-field-config-row-select dbl-field-config-row-select--header';

  return (
    <div className={wrapClass}>
      <Checkbox
        value={isAllSelected}
        indeterminate={isPartialSelected}
        disabled={!hasBatchKeys}
        title={
          variant === 'toolbar'
            ? selectAllText
            : formatMessage({ id: 'dbl_model_agent_step2_col_row_select' })
        }
        onChange={(checked) => {
          if (!checked) {
            const drop = new Set(batchKeys);
            changeFieldsValue(fieldsValue.filter((s) => !drop.has(s)));
          } else {
            changeFieldsValue(
              Array.from(new Set([...fieldsValue, ...batchKeys])),
            );
          }
        }}
      >
        {variant === 'toolbar' ? selectAllText : null}
      </Checkbox>
    </div>
  );
};

export default FieldConfigBatchSelectCheckbox;
